import { useTranslation } from "react-i18next";
import { Button, Icon, Modal } from "semantic-ui-react";

import { NoData, OpenRecalls, PreviousRecalls } from "components/Recalls/components/RecallsModal/components";
import "components/Recalls/components/RecallsModal/RecallsModal.scss";
import { retrieveOpenRecalls } from "components/Recalls/utils";

import type { RECALL_TYPE } from "components/Recalls";
import type { RecallEntity } from "models";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";

export type RecallsModalProps = {
  isOpen: boolean;
  selectOnly: boolean;
  showClosedRecalls: boolean;
  rdwRecalls?: RecallEntity[];
  ponRecalls?: RecallEntity[];
  interventions?: AddInterventionPayload[];
  distrimateRecalls?: RecallEntity[];
  onToggleIsModalOpen: () => void;
  isPONRecallConverted: (recall: RecallEntity) => boolean;
  onConvertRecallToIntervention: (recalls: RecallEntity, recallType: RECALL_TYPE) => void;
  isDistrimateOrRDWRecallConverted: (recall: RecallEntity) => boolean;
  onSelectionConfirm?: () => void;
  onSelectAllRecalls?: (checked: boolean) => void;
  handleSelectionChange?: (recall: RecallEntity, recallType: RECALL_TYPE) => void;
};

export const RecallsModal = ({
  isOpen,
  rdwRecalls,
  selectOnly,
  ponRecalls,
  interventions,
  distrimateRecalls,
  showClosedRecalls,
  onSelectAllRecalls,
  onSelectionConfirm,
  onToggleIsModalOpen,
  isPONRecallConverted,
  handleSelectionChange,
  onConvertRecallToIntervention,
  isDistrimateOrRDWRecallConverted
}: RecallsModalProps) => {
  const { t } = useTranslation();

  const hasOpenRecalls = retrieveOpenRecalls(rdwRecalls).length > 0 || retrieveOpenRecalls(distrimateRecalls).length > 0 || (!!ponRecalls && ponRecalls.length > 0);

  return (
    <Modal centered className="RecallsModal" open={isOpen} size="large">
      <Modal.Header>
        <div className="RecallsModal-header">
          <div>{t("v8_recalls", "Recalls")}</div>
          <Button icon basic onClick={onToggleIsModalOpen}>
            <Icon className="xmark" />
          </Button>
        </div>
      </Modal.Header>
      <Modal.Content className="RecallsModal-content" scrolling>
        <NoData hasData={Boolean(distrimateRecalls?.length) || Boolean(ponRecalls?.length) || Boolean(rdwRecalls?.length)} />
        <OpenRecalls
          ponRecalls={ponRecalls}
          rdwRecalls={rdwRecalls}
          selectOnly={selectOnly}
          interventions={interventions}
          distrimateRecalls={distrimateRecalls}
          onSelectAllRecalls={onSelectAllRecalls}
          isPONRecallConverted={isPONRecallConverted}
          handleSelectionChange={handleSelectionChange}
          onConvertRecallToIntervention={onConvertRecallToIntervention}
          isDistrimateOrRDWRecallConverted={isDistrimateOrRDWRecallConverted}
        />
        {showClosedRecalls && <PreviousRecalls distrimateRecalls={distrimateRecalls} rdwRecalls={rdwRecalls} hasOpenRecalls={hasOpenRecalls} />}
      </Modal.Content>

      {selectOnly && (
        <Modal.Actions className="RecallsModal-actions">
          <Button onClick={onToggleIsModalOpen} basic>
            {t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Button>
          <Button onClick={onSelectionConfirm} className="RecallsModal-actions--confirm">
            {t("v8_confirm", "Confirm")}
            <Icon className="check" />
          </Button>
        </Modal.Actions>
      )}
    </Modal>
  );
};
